class VetinnClinics extends HTMLElement {
  constructor() {
    super();
    this.clinics = [];
    this.filteredClinics = [];
    this.visibleCount = Number(this.dataset.pageSize) || 20;
    this.pageSize = this.visibleCount;
    this.collator = new Intl.Collator('tr', { sensitivity: 'base' });
  }

  connectedCallback() {
    this.searchInput = this.querySelector('[data-clinic-search]');
    this.citySelect = this.querySelector('[data-clinic-city]');
    this.districtSelect = this.querySelector('[data-clinic-district]');
    this.clearButton = this.querySelector('[data-clinic-clear]');
    this.showMoreButton = this.querySelector('[data-clinic-more]');
    this.tableBody = this.querySelector('[data-clinic-table-body]');
    this.cardList = this.querySelector('[data-clinic-cards]');
    this.resultText = this.querySelector('[data-clinic-result-text]');
    this.emptyState = this.querySelector('[data-clinic-empty]');
    this.errorState = this.querySelector('[data-clinic-error]');

    this.searchInput.addEventListener('input', () => this.applyFilters());
    this.citySelect.addEventListener('change', () => {
      this.updateDistrictOptions();
      this.applyFilters();
    });
    this.districtSelect.addEventListener('change', () => this.applyFilters());
    this.clearButton.addEventListener('click', () => this.clearFilters());
    this.showMoreButton.addEventListener('click', () => {
      this.visibleCount += this.pageSize;
      this.render();
    });

    this.loadClinics();
  }

  async loadClinics() {
    try {
      const response = await fetch(this.dataset.source, { credentials: 'same-origin' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const clinics = await response.json();
      if (!Array.isArray(clinics)) throw new Error('Beklenmeyen veri biçimi');

      this.clinics = clinics
        .filter((clinic) => clinic && clinic.name && clinic.city && clinic.district)
        .sort((a, b) =>
          this.collator.compare(a.city, b.city) ||
          this.collator.compare(a.district, b.district) ||
          this.collator.compare(a.name, b.name)
        );

      this.populateSelect(this.citySelect, this.uniqueValues(this.clinics, 'city'));
      this.updateDistrictOptions();
      this.applyFilters();
      this.setAttribute('data-loaded', 'true');
    } catch (error) {
      this.setAttribute('data-loaded', 'error');
      this.errorState.hidden = false;
      this.resultText.textContent = '';
      console.error('Vetinn klinik verileri yüklenemedi:', error);
    }
  }

  normalize(value) {
    return String(value || '')
      .toLocaleLowerCase('tr-TR')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();
  }

  uniqueValues(items, key) {
    return [...new Set(items.map((item) => item[key]).filter(Boolean))].sort(this.collator.compare);
  }

  populateSelect(select, values, selectedValue = '') {
    const defaultOption = select.options[0];
    select.replaceChildren(defaultOption);

    values.forEach((value) => {
      const option = document.createElement('option');
      option.value = value;
      option.textContent = value;
      option.selected = value === selectedValue;
      select.append(option);
    });
  }

  updateDistrictOptions() {
    const selectedDistrict = this.districtSelect.value;
    const selectedCity = this.citySelect.value;
    const candidates = selectedCity
      ? this.clinics.filter((clinic) => clinic.city === selectedCity)
      : this.clinics;
    const districts = this.uniqueValues(candidates, 'district');

    this.populateSelect(
      this.districtSelect,
      districts,
      districts.includes(selectedDistrict) ? selectedDistrict : ''
    );
  }

  applyFilters() {
    const query = this.normalize(this.searchInput.value);
    const city = this.citySelect.value;
    const district = this.districtSelect.value;

    this.filteredClinics = this.clinics.filter((clinic) => {
      const searchable = this.normalize(`${clinic.name} ${clinic.city} ${clinic.district}`);
      return (!query || searchable.includes(query)) &&
        (!city || clinic.city === city) &&
        (!district || clinic.district === district);
    });

    this.visibleCount = this.pageSize;
    this.render();
  }

  clearFilters() {
    this.searchInput.value = '';
    this.citySelect.value = '';
    this.updateDistrictOptions();
    this.districtSelect.value = '';
    this.applyFilters();
    this.searchInput.focus();
  }

  render() {
    const visibleClinics = this.filteredClinics.slice(0, this.visibleCount);
    const hasFilters = Boolean(this.searchInput.value || this.citySelect.value || this.districtSelect.value);

    this.tableBody.replaceChildren(...visibleClinics.map((clinic) => this.createTableRow(clinic)));
    this.cardList.replaceChildren(...visibleClinics.map((clinic) => this.createCard(clinic)));

    this.emptyState.hidden = this.filteredClinics.length !== 0;
    this.showMoreButton.hidden = visibleClinics.length >= this.filteredClinics.length;
    this.clearButton.hidden = !hasFilters;
    this.resultText.textContent = this.filteredClinics.length === 1
      ? '1 klinik bulundu'
      : `${this.filteredClinics.length} klinik bulundu`;
  }

  createTableRow(clinic) {
    const row = document.createElement('tr');
    [clinic.name, clinic.city, clinic.district].forEach((value, index) => {
      const cell = document.createElement('td');
      cell.textContent = value;
      if (index === 0) cell.className = 'vetinn-clinics__name';
      row.append(cell);
    });
    return row;
  }

  createCard(clinic) {
    const card = document.createElement('li');
    card.className = 'vetinn-clinics__card';

    const name = document.createElement('h3');
    name.textContent = clinic.name;

    const location = document.createElement('p');
    location.textContent = `${clinic.district}, ${clinic.city}`;

    card.append(name, location);
    return card;
  }
}

if (!customElements.get('vetinn-clinics')) {
  customElements.define('vetinn-clinics', VetinnClinics);
}
